import { Box, BoxProps, ButtonBase, styled, Typography } from '@mui/material';
import { IComment, IOrganizationConfig } from '@tymely/atoms';
import { useFindIntent, useTicketOrganization } from '@tymely/services';
import React, { useMemo, useState } from 'react';
import { Avatar } from '@tymely/components/Avatar';
import { useClipboard } from 'use-clipboard-copy';
import TyIcon from '@tymely/components/Icons/Ty';

import CommentBubble from './CommentBubble';
import CommentDates from './CommentDates';
import { formatBubbleDate } from './utils';
import { DatetimeTypo } from './styled';

const _DatetimeTypoContainer = styled(Box)`
    display: flex;
    align-items: flex-start;
    padding: ${({ theme }) => theme.spacing(0, 2)};
    position: relative;
`;

const _DatetimeTypo = styled(DatetimeTypo)`
    position: sticky;
    top: ${({ theme }) => theme.spacing(4)};
`;

const _Avatar = styled(Avatar, {
    shouldForwardProp: (prop: string) => !['isTymely', 'customer'].includes(prop),
})<{ isTymely: boolean; customer: boolean }>`
    background-color: ${({ isTymely, customer, theme }) =>
        isTymely ? theme.palette.primary.main : customer ? theme.palette.secondary.main : 'grey'};
    margin-right: ${({ customer, theme }) => (customer ? theme.spacing(2.5) : 0)};
    margin-left: ${({ customer, theme }) => (customer ? 0 : theme.spacing(2.5))};
`;

const _Root = styled(Box, {
    shouldForwardProp: (prop: string) => !['isCustomer', 'organizationConfig'].includes(prop),
})<{
    isCustomer: boolean;
}>`
    display: flex;
    flex-direction: ${({ isCustomer }) => (isCustomer ? 'row' : 'row-reverse')};
    align-self: ${({ isCustomer }) => (isCustomer ? 'flex-start' : 'flex-end')};
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing(2)};

    &:first-of-type {
        margin-top: ${({ theme }) => theme.spacing(2)};
    }
`;

const _HighlightedCommentContainer = styled(Box, {
    shouldForwardProp: (prop: string) => !['isCustomer'].includes(prop),
})<{
    isCustomer: boolean;
}>`
    display: flex;
    flex-direction: ${({ isCustomer }) => (isCustomer ? 'row' : 'row-reverse')};
`;

const CommentContainer = styled(Box, {
    shouldForwardProp: (prop: string) => !['isCustomer'].includes(prop),
})<{
    isCustomer: boolean;
}>`
    display: flex;
    width: 100%;
    flex-direction: ${({ isCustomer }) => (isCustomer ? 'row' : 'row-reverse')};
    position: relative;
`;

type CommentProps = Omit<BoxProps, 'onSelect'> & {
    comment: IComment;
    highlightText?: string;
    selected?: boolean;
    focusedOn?: boolean;
    history?: boolean;
    title?: string;
    onSelect?: (commentId: IComment['id']) => void;
    onDeselect?: () => void;
    onUntag?: (comment: IComment) => void;
    preciseTimestamp?: boolean;
    onToggleConversationMessages?: () => void;
    showConversationMessages?: boolean;
    policyEvaluationEvents?: { label: string; date: string; subtitle?: string }[];
    thinkingContent?: string;
    onToggleThinking?: () => void;
    organizationConfig?: IOrganizationConfig;
};

const _CommentBubble = styled(CommentBubble)`
    max-width: 80%;
`;

const _Details = styled(Box)`
    flex: 1;
`;

const Comment = ({
    comment,
    selected,
    focusedOn,
    onUntag,
    preciseTimestamp,
    highlightText,
    title,
    history,
    onSelect,
    onDeselect,
    onToggleConversationMessages,
    showConversationMessages,
    policyEvaluationEvents,
    thinkingContent,
    onToggleThinking,
    organizationConfig: propsOrganizationConfig,
    ...rest
}: CommentProps) => {
    const intent = useFindIntent(comment.selected_intent_id);
    const needHighlight = Boolean(comment.additional_data?.['highlights']);
    const orgFromHook = useTicketOrganization();
    const config = propsOrganizationConfig ?? orgFromHook?.config;
    const isTymely = !comment.is_customer && comment.author_id === config?.agent?.id;
    const [showDates, setShowDates] = useState(false);
    const username = useMemo(() => {
        if (comment.is_customer) {
            return comment.from_name;
        }
        if (isTymely) {
            return <TyIcon />;
        }
        return 'Customer Service';
    }, [comment.is_customer, comment.from_name, isTymely]);

    const { copy } = useClipboard();
    const handleCopyCommentId = () => {
        copy(String(comment.id));
    };

    return (
        <_Root
            {...rest}
            id={String(comment.id)}
            isCustomer={comment.is_customer}
            onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                comment.is_customer && !selected && onSelect?.(comment.id);
            }}
        >
            <_Avatar size="large" customer={comment.is_customer} username={username} isTymely={isTymely} />
            <_Details>
                <Box
                    sx={{
                        mb: 0.5,
                        gap: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: comment.is_customer ? 'row' : 'row-reverse',
                    }}
                >
                    {comment.from_name && (
                        <Typography
                            variant="subtitle2"
                            color="secondary"
                            sx={{
                                textAlign: comment.is_customer ? 'left' : 'right',
                            }}
                        >
                            {comment.is_automated_response ? 'AUTOMATED' : comment.from_name}
                        </Typography>
                    )}
                    <ButtonBase centerRipple sx={{ p: 0 }} onClick={handleCopyCommentId}>
                        <Typography
                            variant="subtitle2"
                            color="secondary"
                            sx={{
                                opacity: 0.66,
                                cursor: 'pointer',
                            }}
                        >
                            #{comment.id}
                        </Typography>
                    </ButtonBase>
                    {comment.channel && (
                        <Typography
                            variant="caption"
                            color="secondary"
                            sx={{
                                opacity: 0.5,
                                fontSize: '0.75rem',
                                ml: 1,
                                userSelect: 'all',
                            }}
                        >
                            {comment.channel}
                        </Typography>
                    )}
                </Box>
                {!needHighlight && (
                    <CommentContainer isCustomer={comment.is_customer}>
                        <_CommentBubble
                            comment={comment}
                            highlightText={highlightText}
                            intent={intent}
                            title={title}
                            selected={selected}
                            focusedOn={focusedOn}
                            withDate={history}
                            onUntag={onUntag}
                            onToggleConversationMessages={onToggleConversationMessages}
                            showConversationMessages={showConversationMessages}
                            thinkingContent={thinkingContent}
                            onToggleThinking={onToggleThinking}
                        />
                        <_DatetimeTypoContainer>
                            {!showDates && (
                                <_DatetimeTypo
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (comment.is_customer) {
                                            setShowDates(true);
                                        }
                                    }}
                                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {formatBubbleDate(comment.inquiry_date, false, preciseTimestamp)}
                                </_DatetimeTypo>
                            )}
                            {showDates && comment.is_customer && (
                                <CommentDates
                                    policyEvaluationEvents={policyEvaluationEvents}
                                    onClose={() => setShowDates(false)}
                                />
                            )}
                        </_DatetimeTypoContainer>
                    </CommentContainer>
                )}
            </_Details>
        </_Root>
    );
};

export default Comment;
export type { CommentProps };
